import { AGENT_TYPES, UploadDialogState } from "@/modules/agent/types";
import UploadDialog from "@/modules/agent/components/upload-dialog";
import { StatsCard } from "@/modules/agent/components/TPRADashboard/components/StatsCard";
import { useGetTPRASummary } from "@/modules/agent/components/TPRADashboard/requests";
import { transformTPRASummary } from "@/modules/agent/components/TPRADashboard/utils/transformSummaryData";
import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import ErrorBoundary from "@/shared/components/error-boundary";
import { ROUTES } from "@/shared/constants/routes";
import { Button, Card, CardBody, Chip } from "@nextui-org/react";
import { BarChart3, LineChart as LineChartIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export default function TPRAHomePage() {
  const navigate = useNavigate();
  const [chartType, setChartType] = useState<"bar" | "trend">("bar");

  // Modal state
  const [uploadDialogState, setUploadDialogState] = useState<UploadDialogState>({
    isOpen: false,
  });

  // Fetch TPRA summary data
  const { data: summaryData, isLoading, isError, error } = useGetTPRASummary();

  // Transform data for UI consumption
  const transformedData = useMemo(() => {
    if (!summaryData) return null;
    return transformTPRASummary(summaryData);
  }, [summaryData]);

  // Extract data from transformed response
  const metricsData = transformedData?.metrics ?? [];
  const vendorsByRiskData = transformedData?.vendorsByRiskData ?? [];
  const vendorsTrendData = transformedData?.vendorsTrendData ?? [];
  const breachTrendData = transformedData?.breachTrendData ?? [];
  const assessmentStages = transformedData?.assessmentStages ?? [];

  // Handle opening the modal
  const handleNewAssessment = () => {
    setUploadDialogState({
      isOpen: true,
      type: AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    });
  };

  // Handle navigation to breach history
  const handleNavigateToBreachHistory = () => {
    navigate(`/${ROUTES.THIRD_PARTY_BREACH_HISTORY}`);
  };

  return (
    <ErrorBoundary fallback={null}>
      <div className="w-full h-full flex flex-col">
        {/* Top Bar: Breadcrumb + New Assessment Button */}
        <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]">
          <div className="flex gap-4 items-center">
            <ZBreadCrumbs
              items={[
                { title: "Third-Party Risk", href: `/${ROUTES.THIRD_PARTY_HOME}` },
                { title: "Home", href: `/${ROUTES.THIRD_PARTY_HOME}` },
              ]}
            />
          </div>
          <ZButton
            color="primary"
            className="text-xs font-medium h-7 px-3"
            onClick={handleNewAssessment}
          >
            New Assessment
          </ZButton>
        </div>

        {/* Main Content */}
        <div className="h-full w-full overflow-hidden rounded-lg flex flex-col">
          {/* Header: Title */}
          <div className="px-6 py-4 flex flex-col bg-white gap-2">
            <div className="flex flex-row justify-between items-center gap-2 flex-shrink-0">
              <div className="flex flex-row items-center gap-2 flex-grow">
                <h2 className="font-['Inter'] font-medium text-[14px] leading-[20px] text-[#11181C]">
                  Overview
                </h2>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col overflow-auto bg-white px-6 pb-6">
            {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <LoadingSpinner containerClassName="bg-transparent relative" className="mx-auto" />
            </div>
          )}

          {/* Error state */}
          {isError && !isLoading && (
            <div className="flex flex-col items-center justify-center h-full py-8 text-center">
              <p className="text-red-500 text-sm font-medium mb-2">Failed to load dashboard summary</p>
              <p className="text-gray-500 text-xs">
                {error instanceof Error ? error.message : "An unexpected error occurred"}
              </p>
            </div>
          )}

          {/* No data state */}
          {!isLoading && !isError && !transformedData && (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500 text-sm">No data available</p>
            </div>
          )}

          {/* Content */}
          {!isLoading && !isError && transformedData && (
            <div className="space-y-4 grow flex flex-col">
              {/* Metrics */}
              <div className="grid grid-cols-3 gap-4">
                {metricsData.map((metric, index) => (
                  <StatsCard key={index} label={metric.label} value={metric.value} />
                ))}
              </div>

              {/* Charts */}
              <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">
                {/* Vendors by Risk Chart */}
                <Card className="shadow-none border border-[#E4E4E7] rounded-lg flex flex-col">
                  <CardBody className="p-4 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-inter text-xs font-medium leading-5 tracking-normal text-[#71717A]">
                        Third-Parties by Risk
                      </h3>
                      <div className="flex items-center gap-1 bg-[#F4F4F5] rounded p-1">
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          className={`min-w-6 w-6 h-6 ${chartType === "bar" ? "bg-white shadow-sm" : ""}`}
                          onPress={() => setChartType("bar")}
                        >
                          <BarChart3 size={14} className={chartType === "bar" ? "text-[#006FEE]" : "text-[#71717A]"} />
                        </Button>
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          className={`min-w-6 w-6 h-6 ${chartType === "trend" ? "bg-white shadow-sm" : ""}`}
                          onPress={() => setChartType("trend")}
                        >
                          <LineChartIcon size={14} className={chartType === "trend" ? "text-[#006FEE]" : "text-[#71717A]"} />
                        </Button>
                      </div>
                    </div>
                    <div className="flex-1 min-h-[300px] overflow-hidden">
                      <ResponsiveContainer width="100%" height="100%">
                        {chartType === "bar" ? (
                          <BarChart data={vendorsByRiskData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                            <XAxis
                              dataKey="name"
                              tick={{ fontSize: 14, fill: "#71717A", fontWeight: 400 }}
                              axisLine={{ stroke: "#E4E4E7" }}
                              tickLine={false}
                            />
                            <YAxis
                              tick={{ fontSize: 12, fill: "#71717A" }}
                              axisLine={{ stroke: "#E4E4E7" }}
                              tickLine={false}
                              domain={[0, "auto"]}
                            />
                            <Tooltip
                              cursor={{ fill: "rgba(0, 0, 0, 0.02)" }}
                              contentStyle={{
                                backgroundColor: "rgba(0, 0, 0, 0.8)",
                                backdropFilter: "blur(8px)",
                                border: "none",
                                borderRadius: "8px",
                                padding: "8px 12px",
                                fontSize: "12px",
                              }}
                              itemStyle={{ color: "white" }}
                              labelStyle={{ color: "white" }}
                              formatter={(value: number, name: string) => [value, name.charAt(0).toUpperCase() + name.slice(1)]}
                            />
                            <Bar
                              dataKey="value"
                              radius={[4, 4, 0, 0]}
                              barSize={40}
                              label={{
                                position: "top",
                                fill: "#52525B",
                                fontSize: 14,
                                fontWeight: 400,
                              }}
                            >
                              {vendorsByRiskData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        ) : (
                          <LineChart data={vendorsTrendData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                            <XAxis
                              dataKey="month"
                              tick={{ fontSize: 12, fill: "#71717A" }}
                              axisLine={{ stroke: "#E4E4E7" }}
                              tickLine={false}
                            />
                            <YAxis tick={{ fontSize: 12, fill: "#71717A" }} axisLine={{ stroke: "#E4E4E7" }} tickLine={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "rgba(0, 0, 0, 0.8)",
                                backdropFilter: "blur(8px)",
                                border: "none",
                                borderRadius: "8px",
                                padding: "8px 12px",
                                fontSize: "12px",
                              }}
                              itemStyle={{ color: "white" }}
                              labelStyle={{ color: "white" }}
                            />
                            <Legend wrapperStyle={{ paddingTop: "20px", fontSize: "12px" }} iconType="line" />
                            <Line type="monotone" dataKey="All" stroke="#3B82F6" strokeWidth={3} dot={{ fill: "#3B82F6", r: 4 }} />
                            <Line type="monotone" dataKey="High" stroke="#EC4899" strokeWidth={2} dot={{ fill: "#EC4899", r: 3 }} />
                            <Line type="monotone" dataKey="Medium" stroke="#F59E0B" strokeWidth={2} dot={{ fill: "#F59E0B", r: 3 }} />
                            <Line type="monotone" dataKey="Low" stroke="#10B981" strokeWidth={2} dot={{ fill: "#10B981", r: 3 }} />
                            <Line type="monotone" dataKey="Pending" stroke="#9CA3AF" strokeWidth={2} dot={{ fill: "#9CA3AF", r: 3 }} />
                          </LineChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                  </CardBody>
                </Card>

                {/* Breach Trend */}
                <Card className="shadow-none border border-[#E4E4E7] rounded-lg flex flex-col">
                  <CardBody className="p-4 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-inter text-xs font-medium leading-5 tracking-normal text-[#71717A]">Breach Trend</h3>
                      <Button size="sm" variant="light" color="primary" onPress={handleNavigateToBreachHistory}>
                        View All
                      </Button>
                    </div>
                    <div className="flex-1 min-h-[300px] overflow-x-hidden">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={breachTrendData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                          <XAxis
                            dataKey="month"
                            tick={{ fontSize: 14, fill: "#71717A", fontWeight: 400 }}
                            axisLine={{ stroke: "#E4E4E7" }}
                            tickLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: 12, fill: "#71717A" }}
                            axisLine={{ stroke: "#E4E4E7" }}
                            tickLine={false}
                            domain={[0, "auto"]}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "rgba(0, 0, 0, 0.8)",
                              backdropFilter: "blur(8px)",
                              border: "none",
                              borderRadius: "8px",
                              padding: "8px 12px",
                              fontSize: "12px",
                            }}
                            itemStyle={{ color: "white" }}
                            labelStyle={{ color: "white" }}
                            formatter={(value: number) => [value, "Breaches"]}
                          />
                          <Line
                            type="monotone"
                            dataKey="breaches"
                            stroke="#EC4899"
                            strokeWidth={3}
                            dot={{ fill: "#EC4899", r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardBody>
                </Card>
              </div>

              {/* Assessments by Stage */}
              <div className="!mb-4">
                <h3 className="font-inter text-sm font-medium leading-5 tracking-normal mb-4 text-[#71717A]">
                  Assessments by Stage
                </h3>
                <div className="grid grid-cols-5 gap-4">
                  {assessmentStages.map((stage, index) => {
                    const Icon = stage.icon;
                    return (
                      <Card key={index} className="shadow-none border border-[#E4E4E7] rounded-lg cursor-default">
                        <CardBody className="p-4">
                          <div className="font-inter text-lg font-medium leading-7 tracking-normal mb-3 text-[#171717]">
                            {stage.count}
                          </div>
                          <div className="flex items-center gap-2">
                            <Chip
                              variant="bordered"
                              radius="full"
                              className="h-5 w-auto border-1"
                              classNames={{
                                content: "px-1 py-2 flex items-center gap-1",
                              }}
                              style={{
                                color: stage.color,
                                borderColor: stage.color,
                                backgroundColor: stage.bgColor,
                              }}
                            >
                              <Icon size={13} />
                            </Chip>
                            <span className="font-inter text-sm font-normal leading-5 tracking-normal">{stage.label}</span>
                          </div>
                        </CardBody>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <UploadDialog data={uploadDialogState} setIsOpen={setUploadDialogState} />
    </ErrorBoundary>
  );
}
